import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { fetchOnboardingStatus } from '@/services/api/onboarding.api';
import { getUser } from '@/lib/auth';
import { UserRole, type LoginCredentials, type RegisterCredentials } from '@pms/shared';

/**
 * Determine post-auth destination.
 *
 * - super_admin → the BestAcco platform console (/admin). They have no tenant
 *   onboarding and would 403 on every hotel endpoint, so they must never enter
 *   the hotel shell.
 * - everyone else → onboarding wizard if incomplete, otherwise the property hub.
 */
async function resolvePostAuthRoute(): Promise<string> {
  // A pending account (one-time temp password) must set a real one before
  // reaching anything else.
  if (getUser()?.mustChangePassword) {
    return '/set-password';
  }
  if (getUser()?.role === UserRole.SUPER_ADMIN) {
    return '/admin';
  }
  try {
    const status = await fetchOnboardingStatus();
    if (!status.completed) return '/onboarding';
  } catch {
    // If the status endpoint fails (e.g. 404), fall through to the hub
  }
  return '/hub';
}

/**
 * Primary auth hook for components.
 *
 * Wraps AuthContext and adds navigation side-effects so the context
 * itself stays free of router coupling.
 */
export function useAuth() {
  const {
    user,
    isLoading,
    login: contextLogin,
    register: contextRegister,
    logout: contextLogout,
    setPassword: contextSetPassword,
  } = useAuthContext();
  const navigate = useNavigate();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      await contextLogin(credentials);
      const destination = await resolvePostAuthRoute();
      navigate(destination, { replace: true });
    },
    [contextLogin, navigate],
  );

  const register = useCallback(
    async (credentials: RegisterCredentials) => {
      await contextRegister(credentials);
      const destination = await resolvePostAuthRoute();
      navigate(destination, { replace: true });
    },
    [contextRegister, navigate],
  );

  const logout = useCallback(async () => {
    await contextLogout();
    navigate('/login', { replace: true });
  }, [contextLogout, navigate]);

  const setPassword = useCallback(
    async (newPassword: string) => {
      await contextSetPassword(newPassword);
      const destination = await resolvePostAuthRoute();
      navigate(destination, { replace: true });
    },
    [contextSetPassword, navigate],
  );

  return {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    register,
    logout,
    setPassword,
  };
}
